import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../config/firebase';

export type LogSeverity = 'debug' | 'info' | 'warn' | 'error' | 'critical';
export type LogSource = 'frontend';

export type LogContext = Record<string, unknown>;

interface LogOptions {
  error?: unknown;
  persist?: boolean;
  attentionRequired?: boolean;
}

interface SanitizedError {
  name?: string;
  message: string;
  code?: string;
  stack?: string;
}

const persistedSeverities = new Set<LogSeverity>(['warn', 'error', 'critical']);
const maxDepth = 4;
const maxArrayLength = 25;
const sensitiveKeyPattern = /(password|secret|token|key|authorization|credential|cookie)/i;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const sanitizeValue = (value: unknown, depth = 0): unknown => {
  if (value == null) return value;
  if (typeof value === 'string') return value.length > 500 ? `${value.slice(0, 500)}…` : value;
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (value instanceof Date) return value.toISOString();
  if (value instanceof Error) return sanitizeError(value);
  if (depth >= maxDepth) return '[truncated]';
  if (Array.isArray(value)) {
    return value.slice(0, maxArrayLength).map((item) => sanitizeValue(item, depth + 1));
  }
  if (isRecord(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => [
        key,
        sensitiveKeyPattern.test(key) ? '[redacted]' : sanitizeValue(nestedValue, depth + 1),
      ])
    );
  }
  return String(value);
};

export const sanitizeError = (error: unknown): SanitizedError => {
  if (error instanceof Error) {
    const record = error as Error & { code?: unknown };
    const sanitized: SanitizedError = {
      name: error.name,
      message: error.message,
      code: typeof record.code === 'string' ? record.code : undefined,
    };
    if (process.env.NODE_ENV === 'development' && error.stack) {
      sanitized.stack = error.stack;
    }
    return sanitized;
  }

  if (isRecord(error)) {
    const message = typeof error.message === 'string' ? error.message : JSON.stringify(sanitizeValue(error));
    const sanitized: SanitizedError = {
      name: typeof error.name === 'string' ? error.name : undefined,
      message,
      code: typeof error.code === 'string' ? error.code : undefined,
    };
    return sanitized;
  }

  return { message: String(error ?? 'Unknown error') };
};

const shouldPersist = (severity: LogSeverity, options?: LogOptions): boolean => {
  if (options?.persist != null) return options.persist;
  if (persistedSeverities.has(severity)) return true;
  return process.env.REACT_APP_VERBOSE_FIRESTORE_LOGS === 'true';
};

const consoleLog = (severity: LogSeverity, message: string, context?: LogContext, error?: unknown) => {
  const payload = context ? sanitizeValue(context) : undefined;
  const sanitizedError = error ? sanitizeError(error) : undefined;
  switch (severity) {
    case 'debug':
      console.debug(message, payload, sanitizedError);
      break;
    case 'info':
      console.info(message, payload, sanitizedError);
      break;
    case 'warn':
      console.warn(message, payload, sanitizedError);
      break;
    case 'error':
    case 'critical':
      console.error(message, payload, sanitizedError);
      break;
  }
};

const writeLog = async (
  severity: LogSeverity,
  message: string,
  context?: LogContext,
  options?: LogOptions
): Promise<void> => {
  const currentUser = auth.currentUser;
  await addDoc(collection(db, 'logs'), {
    createdAt: serverTimestamp(),
    clientCreatedAt: new Date().toISOString(),
    severity,
    source: 'frontend' satisfies LogSource,
    message,
    context: context ? sanitizeValue(context) : {},
    error: options?.error ? sanitizeError(options.error) : null,
    route: typeof window !== 'undefined' ? window.location.pathname : null,
    userId: currentUser?.uid ?? null,
    authState: currentUser ? 'authenticated' : 'anonymous',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 300) : null,
    attentionRequired: options?.attentionRequired ?? (severity === 'error' || severity === 'critical'),
    acknowledged: false,
    resolved: false,
  });
};

const log = async (
  severity: LogSeverity,
  message: string,
  context?: LogContext,
  options?: LogOptions
): Promise<void> => {
  consoleLog(severity, message, context, options?.error);

  if (!shouldPersist(severity, options)) return;

  try {
    await writeLog(severity, message, context, options);
  } catch (loggingError) {
    console.warn('[loggingService] Failed to write log entry', sanitizeError(loggingError));
  }
};

export const logger = {
  debug: (message: string, context?: LogContext, options?: LogOptions) => log('debug', message, context, options),
  info: (message: string, context?: LogContext, options?: LogOptions) => log('info', message, context, options),
  warn: (message: string, context?: LogContext, options?: LogOptions) => log('warn', message, context, options),
  error: (message: string, context?: LogContext, options?: LogOptions) => log('error', message, context, options),
  critical: (message: string, context?: LogContext, options?: LogOptions) =>
    log('critical', message, context, options),
};

export default logger;
